/* ==========================================================================
   Intake Form API Client
   Task F34: Standalone axios instance for intake form (no JWT auth)
   ========================================================================== */

import axios from 'axios'

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

/**
 * Standalone axios instance for intake endpoints.
 * No JWT auth interceptor — authentication is via token in the URL path.
 */
const intakeClient = axios.create({
  baseURL: BASE_URL,
  timeout: 60000, // 60s for file uploads
})

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface IntakeFormData {
  subscription_id: string
  contact_email: string
  tier: string
  billing_cycle: string
  already_submitted: boolean
}

export interface FileUploadResponse {
  file_url: string
  file_name: string
}

export interface SlugAvailability {
  slug: string
  available: boolean
  suggestion: string | null
}

export interface IntakeSubmitRequest {
  pharmacy_name: string
  admin_email: string
  fax_number: string
  logo_file_url?: string | null
  primary_color?: string | null
  secondary_color?: string | null
  formulary_file_url?: string | null
  formulary_file_name?: string | null
  preferred_slug: string
  notes?: string | null
}

export interface IntakeSubmissionResponse {
  id: string
  subscription_id: string
  pharmacy_name: string
  preferred_slug: string
  status: string
  submitted_at: string
}

/* --------------------------------------------------------------------------
   API Functions
   -------------------------------------------------------------------------- */

export async function getIntakeFormData(token: string): Promise<IntakeFormData> {
  const response = await intakeClient.get<IntakeFormData>(`/intake/${token}`)
  return response.data
}

export async function uploadIntakeFile(
  token: string,
  file: File,
  fileType: 'logo' | 'formulary',
): Promise<FileUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await intakeClient.post<FileUploadResponse>(
    `/intake/${token}/upload?file_type=${fileType}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

export async function checkSlugAvailability(
  token: string,
  slug: string,
): Promise<SlugAvailability> {
  const response = await intakeClient.get<SlugAvailability>(
    `/intake/${token}/check-slug/${slug}`,
  )
  return response.data
}

export async function submitIntakeForm(
  token: string,
  data: IntakeSubmitRequest,
): Promise<IntakeSubmissionResponse> {
  const response = await intakeClient.post<IntakeSubmissionResponse>(
    `/intake/${token}`,
    data,
  )
  return response.data
}
